import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { Settings, X, Camera, Bell, BellOff, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { BarberPortfolioManager } from './BarberPortfolioManager';
import { notificationService } from '../../../services/notificationService';
import { UserProfile, PortfolioItem } from '../../../types';
import { compressImage } from '../../../lib/imageUtils';

interface ProfileSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: UserProfile | null;
  onUpdate: (data: any) => void;
}

export const ProfileSettingsModal = ({ isOpen, onClose, user, onUpdate }: ProfileSettingsModalProps) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    gender: user?.gender || 'pria',
    avatar: user?.avatar || '',
    bio: user?.bio || '',
    price: user?.price || 0,
    province: user?.province || '',
    city: user?.city || '',
    district: user?.district || '',
    specialties: user?.specialties?.join(', ') || '',
    notificationSettings: user?.notificationSettings || {
      newBooking: true,
      statusUpdate: true,
      newChat: true
    }
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Mohon pilih file gambar');
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const compressed = await compressImage(reader.result as string, 400, 400, 0.8); // Smaller for avatar
      setFormData({ ...formData, avatar: compressed });
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setHasPermission(Notification.permission === 'granted');
    }
  }, []);

  const handleRequestPermission = async () => {
    const granted = await notificationService.requestPermission();
    setHasPermission(granted);
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        gender: user.gender || 'pria',
        avatar: user.avatar || '',
        bio: user.bio || '',
        price: user.price || 0,
        province: user.province || '',
        city: user.city || '',
        district: user.district || '',
        specialties: user.specialties?.join(', ') || '',
        notificationSettings: user.notificationSettings || {
          newBooking: true,
          statusUpdate: true,
          newChat: true
        }
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await onUpdate({
      ...formData,
      specialties: formData.specialties.split(',').map((s: string) => s.trim()).filter(Boolean)
    });
    setIsSaving(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-4">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
      />
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative bg-white w-full sm:max-w-lg h-full sm:h-auto sm:max-h-[90vh] sm:rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col"
      >
        <div className="px-6 sm:px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/30 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shrink-0">
              <Settings className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <p className="font-black text-slate-900 text-base sm:text-lg tracking-tight truncate">Pengaturan Profil</p>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest truncate">Sesuaikan profil publik Anda</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition-colors shrink-0">
            <X className="w-6 h-6 text-slate-400" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6 pb-32 sm:pb-8">
            <div className="flex justify-center mb-8">
              <input 
                type="file"
                ref={fileInputRef}
                onChange={handleAvatarChange}
                accept="image/*"
                className="hidden"
              />
              <div 
                className="relative group cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="relative">
                  <img 
                    src={formData.avatar} 
                    alt="Preview" 
                    className={`w-24 h-24 sm:w-28 sm:h-28 rounded-3xl object-cover ring-4 ring-slate-50 shadow-lg transition-opacity ${isUploading ? 'opacity-50' : 'opacity-100'}`}
                    onError={(e: any) => { e.target.src = 'https://api.dicebear.com/7.x/avataaars/svg?seed=fallback'; }}
                  />
                  {isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
                    </div>
                  )}
                  {formData.avatar && !isUploading && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm('Hapus foto profil?')) {
                          setFormData({ ...formData, avatar: '' });
                        }
                      }}
                      className="absolute -top-2 -right-2 p-1.5 bg-red-500 text-white rounded-lg shadow-lg hover:bg-red-600 transition-all z-10"
                      title="Hapus foto"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {!isUploading && (
                  <div className="absolute inset-0 bg-black/40 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white gap-1">
                    <Camera className="w-6 h-6" />
                    <span className="text-[8px] font-black uppercase tracking-widest">Ubah Foto</span>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-5">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Nama Lengkap</label>
                  <input 
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold focus:border-emerald-500 transition-all outline-none"
                    placeholder="Masukkan nama lengkap"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">URL Foto Profil</label>
                  <input 
                    type="text"
                    value={formData.avatar}
                    onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                    placeholder="https://example.com/photo.jpg"
                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold focus:border-emerald-500 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Jenis Kelamin</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, gender: 'pria' })}
                      className={cn(
                        "py-4 rounded-2xl border-2 font-bold text-sm transition-all focus:outline-none flex items-center justify-center gap-2 cursor-pointer",
                        formData.gender === 'pria' 
                          ? "border-emerald-500 bg-emerald-50/50 text-emerald-700 font-extrabold shadow-sm" 
                          : "border-slate-100 bg-slate-50 text-slate-600 hover:bg-slate-100"
                      )}
                    >
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      Pria
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, gender: 'wanita' })}
                      className={cn(
                        "py-4 rounded-2xl border-2 font-bold text-sm transition-all focus:outline-none flex items-center justify-center gap-2 cursor-pointer",
                        formData.gender === 'wanita' 
                          ? "border-pink-500 bg-pink-50/50 text-pink-700 font-extrabold shadow-sm" 
                          : "border-slate-100 bg-slate-50 text-slate-600 hover:bg-slate-100"
                      )}
                    >
                      <span className="w-2.5 h-2.5 rounded-full bg-pink-500" />
                      Wanita
                    </button>
                  </div>
                </div>
                {user?.role === 'barber' && (
                  <>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Bio Singkat</label>
                      <textarea 
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        rows={3}
                        placeholder="Ceritakan sedikit tentang keahlian & pengalaman Anda..."
                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold focus:border-emerald-500 transition-all outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Spesialisasi (pisahkan koma)</label>
                      <input 
                        type="text"
                        value={formData.specialties}
                        onChange={(e) => setFormData({ ...formData, specialties: e.target.value })}
                        placeholder="Fading, Hair Tattoo, Shaving"
                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 text-sm font-bold focus:border-emerald-500 transition-all outline-none"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Provinsi</label>
                    <input 
                      type="text"
                      value={formData.province}
                      onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                      placeholder="Contoh: Jawa Barat"
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3.5 text-sm font-bold focus:border-emerald-500 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Kota</label>
                    <input 
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Contoh: Bandung"
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3.5 text-sm font-bold focus:border-emerald-500 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Kecamatan</label>
                    <input 
                      type="text"
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                      placeholder="Contoh: Coblong"
                      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3.5 text-sm font-bold focus:border-emerald-500 transition-all outline-none"
                    />
                  </div>
                </div>

                {user?.role === 'barber' && (
                  <div className="space-y-5">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">Harga Utama (Rp)</label>
                    <div className="relative">
                      <span className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 font-black text-sm">Rp</span>
                      <input 
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                        placeholder="50000"
                        className="w-full bg-slate-900 text-white rounded-2xl pl-14 pr-6 py-4 text-sm font-black focus:ring-4 focus:ring-emerald-500/20 transition-all outline-none"
                      />
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-100">
                    <BarberPortfolioManager 
                      userId={user?.uid || ''}
                    />
                  </div>
                </div>
              )}
              </div>
            </div>

            <div className="space-y-4 pt-6 mt-4 border-t border-slate-100">
              <div className="flex items-center justify-between bg-slate-50 p-4 rounded-2xl">
                <div>
                  <p className="text-sm font-black text-slate-900 leading-none mb-1">Push Notifications</p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Izinkan di browser Anda</p>
                </div>
                {hasPermission ? (
                  <div className="flex items-center gap-2 text-emerald-500 bg-emerald-50 px-3 py-1.5 rounded-full">
                    <Bell className="w-3.5 h-3.5" />
                    <span className="text-[10px] font-black uppercase">Aktif</span>
                  </div>
                ) : (
                  <button 
                    type="button"
                    onClick={handleRequestPermission}
                    className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:scale-105 transition-transform"
                  >
                    <BellOff className="w-3.5 h-3.5" />
                    Aktifkan
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {[
                  { id: 'newBooking', label: 'Pesanan Baru', desc: 'Alert saat ada pesanan masuk', role: 'barber' },
                  { id: 'statusUpdate', label: 'Update Status', desc: 'Alert saat status berubah', role: 'customer' },
                  { id: 'newChat', label: 'Pesan Chat', desc: 'Notifikasi pesan baru', role: 'any' }
                ].filter(n => n.role === 'any' || n.role === user?.role).map((pref) => (
                  <label key={pref.id} className="flex items-center justify-between cursor-pointer group bg-white border border-slate-100 p-4 rounded-2xl hover:border-emerald-200 hover:bg-emerald-50/10 transition-all">
                    <div className="min-w-0 flex-1 pr-4">
                      <p className="text-xs font-black text-slate-900 uppercase tracking-tight">{pref.label}</p>
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest opacity-60">{pref.desc}</p>
                    </div>
                    <div className="relative inline-flex items-center shrink-0">
                      <input 
                        type="checkbox"
                        className="sr-only peer"
                        checked={!!(formData.notificationSettings as any)?.[pref.id]}
                        onChange={(e) => setFormData({
                          ...formData,
                          notificationSettings: {
                            ...(formData.notificationSettings || {
                              newBooking: true,
                              statusUpdate: true,
                              newChat: true
                            }),
                            [pref.id]: e.target.checked
                          }
                        })}
                      />
                      <div className="w-10 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500 shadow-inner"></div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </form>
        </div>

        <div className="p-6 sm:p-8 bg-white border-t border-slate-50 shrink-0">
          <button 
            onClick={handleSubmit}
            disabled={isSaving}
            className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10 active:scale-95 disabled:opacity-50"
          >
            {isSaving ? 'Sedang Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};
